import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './LawyerProjectConversation.module.css';
import LawyerProjectMessageComposer from './LawyerProjectMessageComposer';
import {InlineSvgIcon} from './InlineSvgIcon';
import './lawyerProjectThreadScope.css';
import {getProjectSocket} from '../libs/socket';
import {axios} from '../libs/axios';
import {useTranslation} from '../i18n/I18nContext';
import {plainTextFromDescription} from '../libs/htmlToPlainText';

const DRAFT_SAVE_DEBOUNCE_MS = 600;
const MARK_READ_DELAY_MS = 5000;

function stripHtml(html) {
  return plainTextFromDescription(html);
}

function formatProjectMsgDate(value) {
  if (!value) {
    return '';
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

function fileLabel(f) {
  if (!f) {
    return '';
  }
  if (typeof f === 'string') {
    return f;
  }
  return f.name || '';
}

function normalizeThreadFile(f) {
  if (!f) {
    return null;
  }
  if (typeof f === 'string') {
    const name = f.trim();
    return name ? {name, url: null, id: null} : null;
  }
  const name = (f.name && String(f.name).trim()) || '';
  if (!name) {
    return null;
  }
  return {
    name,
    url: f.url ? String(f.url) : null,
    id: f.id ? String(f.id) : null,
  };
}

/** Client-sent row is unread by lawyer until read_by_lawyer_at is set. */
function isClientMessageUnreadByLawyer(c) {
  return c.read_by_lawyer_at == null;
}

function clientIncomingUiFields(c) {
  const unread = isClientMessageUnreadByLawyer(c);
  return {
    read_by_lawyer_at: c.read_by_lawyer_at,
    isUnreadByLawyer: unread,
    playReadFlash: false,
  };
}

/** Lawyer-sent row status fields for CRM tickers. */
function lawyerOutgoingUiFields(c) {
  return {
    authorLabel: c.sender_name || 'Hugo jurist',
    notificationSentAt: c.notification_sent_at || null,
    notificationFailedAt: c.notification_failed_at || null,
    readAt: c.read_at || null,
  };
}

function resolveMessageStatusTooltipKey(msg) {
  if (msg.readAt) {
    return 'project.thread.statusRead';
  }
  if (msg.isRetryingNotification) {
    return 'project.thread.retrying';
  }
  if (msg.notificationFailedAt) {
    return 'project.thread.statusNotifyFailed';
  }
  if (msg.notificationSentAt) {
    return 'project.thread.statusNotified';
  }
  return 'project.thread.statusSent';
}

/** Map DB communications to CRM UI rows (lawyer outgoing / client incoming). */
function normalizeLawyerCrmMessages(comms, projectDescription = '', projectCreatedAt = '') {
  const rows = [];
  (comms || []).forEach((c, idx) => {
    const id = c._id != null ? String(c._id) : `row-${idx}`;
    const ts = c.sent_at || c.createdAt;
    const sortTime = ts ? new Date(ts).getTime() : 0;
    const filesRaw = Array.isArray(c.files) ? c.files : [];
    const files = filesRaw.map(normalizeThreadFile).filter(Boolean);

    if (c.sender_role === 'lawyer') {
      rows.push({
        id,
        type: 'lawyer',
        text: (c.message && String(c.message)) || '',
        files,
        payload: c.payload || null,
        sentAt: formatProjectMsgDate(ts),
        ...lawyerOutgoingUiFields(c),
        _sort: sortTime,
      });
      return;
    }
    if (c.sender_role === 'client') {
      rows.push({
        id,
        type: 'client',
        text: (c.message && String(c.message)) || '',
        files,
        payload: c.payload || null,
        receivedAt: formatProjectMsgDate(ts),
        authorLabel: 'Klient kirjutas',
        ...clientIncomingUiFields(c),
        _sort: sortTime,
      });
      return;
    }
    if (c.type === 'initial_response' && c.message) {
      rows.push({
        id,
        type: 'lawyer',
        text: stripHtml(String(c.message)),
        files: [],
        payload: c.payload || null,
        sentAt: formatProjectMsgDate(c.sent_at),
        ...lawyerOutgoingUiFields(c),
        _sort: c.sent_at ? new Date(c.sent_at).getTime() : sortTime,
      });
    }
  });
  const descTrimmed = String(projectDescription || '').trim();
  const descPlain = stripHtml(descTrimmed).trim();
  if (descPlain) {
    const descSortTime = projectCreatedAt ? new Date(projectCreatedAt).getTime() : 0;
    rows.push({
      id: 'project-description',
      type: 'client',
      text: descPlain,
      files: [],
      receivedAt: formatProjectMsgDate(projectCreatedAt),
      authorLabel: 'Klient kirjeldus',
      isUnreadByLawyer: false,
      playReadFlash: false,
      _sort: descSortTime,
    });
  }
  rows.sort((a, b) => b._sort - a._sort);
  return rows.map(({_sort, ...rest}) => rest);
}

function rawCommToLawyerUiRow(row) {
  if (row.sender_role === 'lawyer') {
    return {
      id: String(row._id || row.id || Math.random()),
      type: 'lawyer',
      text: row.message || '',
      files: (row.files || []).map(normalizeThreadFile).filter(Boolean),
      payload: row.payload || null,
      sentAt: formatProjectMsgDate(row.sent_at),
      ...lawyerOutgoingUiFields(row),
    };
  }
  if (row.sender_role === 'client') {
    return {
      id: String(row._id || row.id || Math.random()),
      type: 'client',
      text: row.message || '',
      files: (row.files || []).map(normalizeThreadFile).filter(Boolean),
      payload: row.payload || null,
      receivedAt: formatProjectMsgDate(row.sent_at),
      authorLabel: 'Klient kirjutas',
      ...clientIncomingUiFields(row),
    };
  }
  return null;
}

function FileList({files}) {
  if (!files || files.length === 0) {
    return null;
  }
  return (
    <div className={styles.fileList}>
      <span className={styles.fileListLabel}>Lisatud failid:</span>
      {files.map((f, idx) => {
        const name = typeof f === 'string' ? f : f?.name || '';
        const url = typeof f === 'object' && f?.url ? f.url : null;
        const key = url || `${name}-${idx}`;
        if (url) {
          return (
            <a key={key} href={url} className={styles.fileLink} target="_blank" rel="noreferrer">
              {name}
            </a>
          );
        }
        return (
          <span key={key} className={styles.fileLink}>
            {name}
          </span>
        );
      })}
    </div>
  );
}

function PayloadCard({payload, readOnly = false}) {
  if (!payload || payload.type !== 'payment_link') {
    return null;
  }
  const statusLabel =
    payload.status === 'accepted'
      ? 'Makstud'
      : payload.status === 'declined'
        ? 'Tagasi lükatud'
        : payload.status === 'expired'
          ? 'Aegunud'
          : 'Ootel';
  const pdfLabel = payload.pdf_label || 'Laadi alla PDF arve';
  return (
    <div className={styles.payloadCard}>
      {readOnly ? (
        <>
          <span className={styles.payloadReadOnly}>
            {payload.label || 'Makselink'}
            {payload.status ? ` (${statusLabel})` : ''}
          </span>
          {payload.pdf_url ? (
            <a
              href={payload.pdf_url}
              className={styles.payloadPdfLink}
              target="_blank"
              rel="noreferrer"
            >
              {pdfLabel}
            </a>
          ) : null}
        </>
      ) : (
        <>
          {payload.url ? (
            <a href={payload.url} className={styles.payloadLink}>
              {payload.label || payload.url}
            </a>
          ) : null}
          {payload.pdf_url ? (
            <a
              href={payload.pdf_url}
              className={styles.payloadPdfLink}
              target="_blank"
              rel="noreferrer"
            >
              {pdfLabel}
            </a>
          ) : null}
        </>
      )}
    </div>
  );
}

function CheckTickIcon({className}) {
  return (
    <svg
      className={className}
      width="12"
      height="10"
      viewBox="0 0 12 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M1 5.2L4.2 8.4L11 1.4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AlertTriangleIcon({className}) {
  return (
    <svg
      className={className}
      width="12"
      height="11"
      viewBox="0 0 12 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M6 1L11.2 9.5H0.8L6 1Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M6 4.2V6.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="6" cy="8.1" r="0.55" fill="currentColor" />
    </svg>
  );
}

function MessageStatusTickers({
  notificationSentAt,
  notificationFailedAt,
  readAt,
  statusTooltipText = '',
  isRetryingNotification = false,
  onRetryNotification,
}) {
  if (readAt) {
    return (
      <span className={styles.sentTickers} aria-label={statusTooltipText}>
        <CheckTickIcon className={styles.tickBlue} />
        <CheckTickIcon className={styles.tickBlue} />
      </span>
    );
  }
  if (notificationFailedAt || isRetryingNotification) {
    return (
      <span
        className={[
          styles.sentTickers,
          styles.sentTickersClickable,
          isRetryingNotification ? styles.sentTickersPending : null,
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={(e) => {
          e.stopPropagation();
          if (!isRetryingNotification) {
            onRetryNotification?.();
          }
        }}
        aria-label={statusTooltipText}
      >
        <AlertTriangleIcon className={styles.tickFail} />
      </span>
    );
  }
  if (notificationSentAt) {
    return (
      <span className={styles.sentTickers} aria-label={statusTooltipText}>
        <CheckTickIcon className={styles.tickGray} />
        <CheckTickIcon className={styles.tickGray} />
      </span>
    );
  }
  return (
    <span className={styles.sentTickers} aria-label={statusTooltipText}>
      <CheckTickIcon className={styles.tickGray} />
    </span>
  );
}

function IncomingBubble({
  text,
  files,
  receivedAt,
  authorLabel,
  isUnreadByLawyer = false,
  playReadFlash = false,
  onReadFlashEnd,
}) {
  useEffect(() => {
    if (!playReadFlash || typeof window === 'undefined') {
      return undefined;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      onReadFlashEnd?.();
    }
    return undefined;
  }, [playReadFlash, onReadFlashEnd]);

  const bubbleClass = [
    styles.recvMsg,
    isUnreadByLawyer ? styles.recvMsgUnread : null,
    playReadFlash ? styles.recvMsgReadFlash : null,
  ]
    .filter(Boolean)
    .join(' ');

  const handleAnimationEnd = (e) => {
    if (e.target !== e.currentTarget) {
      return;
    }
    onReadFlashEnd?.();
  };

  return (
    <div className={styles.recvOuter}>
      <div
        className={bubbleClass}
        onAnimationEnd={playReadFlash ? handleAnimationEnd : undefined}
      >
        <div className={styles.recvMeta}>
          <span className={styles.recvAuthor}>{authorLabel}</span>
          {receivedAt ? <span className={styles.recvDate}>{receivedAt}</span> : null}
        </div>
        {text ? <p className={styles.recvBody}>{text}</p> : null}
        <FileList files={files} />
      </div>
      {isUnreadByLawyer ? (
        <div className={styles.recvStatus}>
          <span className={styles.unreadLabel}>Lugemata</span>
        </div>
      ) : null}
    </div>
  );
}

function OutgoingBubble({
  text,
  files,
  payload,
  sentAt,
  authorLabel,
  notificationSentAt = null,
  notificationFailedAt = null,
  readAt = null,
  statusTooltipText = '',
  isRetryingNotification = false,
  onShowTooltip,
  onHideTooltip,
  onRetryNotification,
}) {
  return (
    <div className={styles.sentMsg}>
      <div className={styles.sentMeta}>
        <span className={styles.sentAuthor}>{authorLabel}</span>
        {sentAt ? (
          <div
            className={styles.sentDateRowWrap}
            onMouseEnter={(e) => onShowTooltip?.(e, statusTooltipText)}
            onMouseLeave={() => onHideTooltip?.()}
          >
            <div className={styles.sentDateRow}>
              <span className={styles.sentDate}>{sentAt}</span>
              <MessageStatusTickers
                notificationSentAt={notificationSentAt}
                notificationFailedAt={notificationFailedAt}
                readAt={readAt}
                statusTooltipText={statusTooltipText}
                isRetryingNotification={isRetryingNotification}
                onRetryNotification={onRetryNotification}
              />
            </div>
          </div>
        ) : null}
      </div>
      {text ? <p className={styles.sentBody}>{text}</p> : null}
      <FileList files={files} />
      <PayloadCard payload={payload} readOnly />
    </div>
  );
}

/**
 * Lawyer CRM project thread (real data + Socket.IO).
 * @param {{
 *   projectId: string;
 *   initialCommunications?: unknown[];
 *   projectDescription?: string;
 *   projectCreatedAt?: string;
 *   currentUser?: { role?: string } | null;
 *   onThreadHeadChange?: (arg: { lastMessageFromClient: boolean }) => void;
 *   projectTitle?: string;
 *   projectFilesUrl?: string;
 *   onAddProjectFilesClick?: () => void;
 *   isUploadingProjectFiles?: boolean;
 *   canAddProjectFiles?: boolean;
 *   addProjectFilesTitle?: string;
 *   initialComposerDraft?: string;
 *   onOpenProjectDetails?: () => void;
 *   isProjectStarted?: boolean;
 *   onLawyerMessageSent?: () => void;
 *   ownerEmail?: string;
 *   onProjectTitleSave?: (title: string) => Promise<boolean>;
 * }} props
 */
const LawyerProjectConversation = forwardRef(function LawyerProjectConversation(
  {
    projectId,
    initialCommunications = [],
    projectDescription = '',
    projectCreatedAt = '',
    currentUser = null,
    onThreadHeadChange,
    projectTitle = '',
    projectFilesUrl = '',
    onAddProjectFilesClick,
    isUploadingProjectFiles = false,
    canAddProjectFiles = true,
    addProjectFilesTitle = '',
    initialComposerDraft = '',
    onOpenProjectDetails,
    isProjectStarted = true,
    onLawyerMessageSent,
    ownerEmail = '',
    onProjectTitleSave,
  },
  ref,
) {
  const {t} = useTranslation();
  const [messages, setMessages] = useState(() =>
    normalizeLawyerCrmMessages(initialCommunications, projectDescription, projectCreatedAt),
  );
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(projectTitle);
  const [savingTitle, setSavingTitle] = useState(false);
  const titleInputRef = useRef(null);
  const [msgTooltip, setMsgTooltip] = useState({visible: false, x: 0, y: 0, message: ''});
  const draftSaveTimerRef = useRef(null);
  const markReadTimerRef = useRef(null);
  const lastSavedDraftRef = useRef(initialComposerDraft);

  const commSeedKey = useMemo(
    () => `${projectId}:${projectDescription}:${projectCreatedAt}:${JSON.stringify(initialCommunications || [])}`,
    [projectId, projectDescription, projectCreatedAt, initialCommunications],
  );

  useEffect(() => {
    setMessages(normalizeLawyerCrmMessages(initialCommunications, projectDescription, projectCreatedAt));
  }, [commSeedKey, initialCommunications, projectDescription, projectCreatedAt]);

  useEffect(() => {
    lastSavedDraftRef.current = initialComposerDraft;
  }, [projectId, initialComposerDraft]);

  useEffect(() => {
    if (!editingTitle) {
      setTitleDraft(projectTitle);
    }
  }, [projectTitle, editingTitle]);

  useEffect(() => {
    if (editingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [editingTitle]);

  const canEditTitle = typeof onProjectTitleSave === 'function';

  const startTitleEdit = useCallback(() => {
    if (!canEditTitle || savingTitle) {
      return;
    }
    setTitleDraft(projectTitle);
    setEditingTitle(true);
  }, [canEditTitle, projectTitle, savingTitle]);

  const cancelTitleEdit = useCallback(() => {
    setTitleDraft(projectTitle);
    setEditingTitle(false);
  }, [projectTitle]);

  const saveTitleEdit = useCallback(async () => {
    if (!canEditTitle || savingTitle) {
      return;
    }
    const trimmed = String(titleDraft || '').trim();
    if (!trimmed) {
      cancelTitleEdit();
      return;
    }
    if (trimmed === String(projectTitle || '').trim()) {
      setEditingTitle(false);
      return;
    }
    setSavingTitle(true);
    try {
      const saved = await onProjectTitleSave(trimmed);
      if (saved) {
        setEditingTitle(false);
      }
    } finally {
      setSavingTitle(false);
    }
  }, [canEditTitle, cancelTitleEdit, onProjectTitleSave, projectTitle, savingTitle, titleDraft]);

  useEffect(
    () => () => {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
        draftSaveTimerRef.current = null;
      }
      if (markReadTimerRef.current) {
        clearTimeout(markReadTimerRef.current);
        markReadTimerRef.current = null;
      }
    },
    [],
  );

  const persistComposerDraft = useCallback(
    async (text) => {
      if (!projectId) {
        return;
      }
      const trimmed = String(text || '').trim();
      if (trimmed === lastSavedDraftRef.current) {
        return;
      }
      const res = await axios.put(
        `/projects/${encodeURIComponent(projectId)}/lawyer-message-draft`,
        {message: text},
      );
      if (res?.ok) {
        lastSavedDraftRef.current = trimmed;
      }
    },
    [projectId],
  );

  const handleComposerDraftChange = useCallback(
    (text) => {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
      }
      draftSaveTimerRef.current = setTimeout(() => {
        draftSaveTimerRef.current = null;
        void persistComposerDraft(text);
      }, DRAFT_SAVE_DEBOUNCE_MS);
    },
    [persistComposerDraft],
  );

  useLayoutEffect(() => {
    if (typeof onThreadHeadChange !== 'function') {
      return;
    }
    const head = messages[0];
    onThreadHeadChange({lastMessageFromClient: head ? head.type === 'client' : false});
  }, [messages, onThreadHeadChange]);

  const appendIfNew = useCallback((raw) => {
    const ui = rawCommToLawyerUiRow(raw);
    if (!ui) {
      return;
    }
    setMessages((prev) => {
      if (prev.some((m) => m.id === ui.id)) {
        return prev;
      }
      return [ui, ...prev];
    });
  }, []);

  const applyClientMessagesReadByLawyer = useCallback((readAt) => {
    const readAtValue = readAt || new Date();
    setMessages((prev) =>
      prev.map((m) => {
        if (m.type !== 'client' || m.id === 'project-description' || !m.isUnreadByLawyer) {
          return m;
        }
        return {
          ...m,
          isUnreadByLawyer: false,
          read_by_lawyer_at: readAtValue,
          playReadFlash: true,
        };
      }),
    );
  }, []);

  const applyLawyerMessagesReadByClient = useCallback((readAt) => {
    const readAtValue = readAt || new Date();
    setMessages((prev) =>
      prev.map((m) => {
        if (m.type !== 'lawyer' || m.readAt) {
          return m;
        }
        return {
          ...m,
          readAt: readAtValue,
        };
      }),
    );
  }, []);

  const applyNotificationStatusUpdated = useCallback((payload) => {
    if (!payload) {
      return;
    }
    const {status, at} = payload;
    if (!at) {
      return;
    }
    setMessages((prev) =>
      prev.map((m) => {
        if (m.type !== 'lawyer' || m.readAt) {
          return m;
        }
        if (status === 'sent' && !m.notificationSentAt) {
          return {
            ...m,
            notificationSentAt: at,
            notificationFailedAt: null,
            isRetryingNotification: false,
          };
        }
        if (status === 'failed' && !m.notificationSentAt) {
          return {
            ...m,
            notificationFailedAt: at,
            isRetryingNotification: false,
          };
        }
        return m;
      }),
    );
  }, []);

  const showMsgTooltip = useCallback((event, message) => {
    setMsgTooltip({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      message: message || '',
    });
  }, []);

  const hideMsgTooltip = useCallback(() => {
    setMsgTooltip({visible: false, x: 0, y: 0, message: ''});
  }, []);

  const handleRetryNotification = useCallback(() => {
    if (!projectId) {
      return;
    }
    hideMsgTooltip();
    setMessages((prev) =>
      prev.map((m) => {
        if (m.type !== 'lawyer' || m.readAt || !m.notificationFailedAt) {
          return m;
        }
        return {
          ...m,
          isRetryingNotification: true,
        };
      }),
    );
    void (async () => {
      try {
        const res = await axios.post(
          `/projects/${encodeURIComponent(projectId)}/messages/resend-notification`,
          {},
        );
        if (!res?.ok) {
          throw new Error('Retry failed');
        }
      } catch {
        setMessages((prev) =>
          prev.map((m) => {
            if (!m.isRetryingNotification) {
              return m;
            }
            return {
              ...m,
              isRetryingNotification: false,
              notificationFailedAt: m.notificationFailedAt || new Date(),
            };
          }),
        );
      }
    })();
  }, [projectId, hideMsgTooltip]);

  const clearReadFlash = useCallback((messageId) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? {...m, playReadFlash: false} : m)),
    );
  }, []);

  /** Debounced mark-read while thread is open; resets on each new client message (Option A). */
  const scheduleMarkReadByLawyer = useCallback(() => {
    if (!projectId || currentUser?.role === 'admin') {
      return;
    }
    if (markReadTimerRef.current) {
      clearTimeout(markReadTimerRef.current);
    }
    markReadTimerRef.current = setTimeout(() => {
      markReadTimerRef.current = null;
      void (async () => {
        const res = await axios.post(
          `/projects/${encodeURIComponent(projectId)}/messages/mark-read-by-lawyer`,
          {},
        );
        if (res?.ok) {
          applyClientMessagesReadByLawyer(new Date());
        }
      })();
    }, MARK_READ_DELAY_MS);
  }, [projectId, currentUser?.role, applyClientMessagesReadByLawyer]);

  useEffect(() => {
    scheduleMarkReadByLawyer();
    return () => {
      if (markReadTimerRef.current) {
        clearTimeout(markReadTimerRef.current);
        markReadTimerRef.current = null;
      }
    };
  }, [scheduleMarkReadByLawyer]);

  useEffect(() => {
    if (!projectId) {
      return undefined;
    }
    const socket = getProjectSocket();
    const roomId = String(projectId);
    const onNew = (payload) => {
      appendIfNew(payload);
      if (payload?.sender_role === 'client') {
        scheduleMarkReadByLawyer();
      }
    };
    const onMessagesRead = (payload) => {
      if (!payload || String(payload.projectId) !== roomId) {
        return;
      }
      if (payload.clientMessagesReadByLawyerAt) {
        if (markReadTimerRef.current) {
          clearTimeout(markReadTimerRef.current);
          markReadTimerRef.current = null;
        }
        applyClientMessagesReadByLawyer(payload.clientMessagesReadByLawyerAt);
      }
      if (payload.lawyerMessagesReadAt) {
        applyLawyerMessagesReadByClient(payload.lawyerMessagesReadAt);
      }
    };
    const onNotificationStatusUpdated = (payload) => {
      if (!payload || String(payload.projectId) !== roomId) {
        return;
      }
      applyNotificationStatusUpdated(payload);
    };
    socket.connect();
    socket.emit('join_project', roomId);
    socket.on('new_message', onNew);
    socket.on('messages_read', onMessagesRead);
    socket.on('notification_status_updated', onNotificationStatusUpdated);
    return () => {
      socket.emit('leave_project', roomId);
      socket.off('new_message', onNew);
      socket.off('messages_read', onMessagesRead);
      socket.off('notification_status_updated', onNotificationStatusUpdated);
      socket.disconnect();
    };
  }, [
    projectId,
    appendIfNew,
    applyClientMessagesReadByLawyer,
    applyLawyerMessagesReadByClient,
    applyNotificationStatusUpdated,
    scheduleMarkReadByLawyer,
  ]);

  const handleSend = useCallback(
    async (text, files = []) => {
      const formData = new FormData();
      formData.append('message', text || '');
      if (ownerEmail) {
        formData.append('ownerEmail', ownerEmail);
      }
      const fileList = Array.isArray(files) ? files.filter(Boolean) : [];
      for (const file of fileList) {
        formData.append('files', file);
      }

      const res = await axios.post(`/projects/${projectId}/uploadAndSendMessage`, formData);
      if (res && res.status && res.status >= 400) {
        throw new Error(
          (typeof res.message === 'string' && res.message) || res.error || 'Send failed',
        );
      }
      if (res && res.message && (res.message._id != null || res.message.sender_role)) {
        appendIfNew(res.message);
        lastSavedDraftRef.current = '';
        if (typeof onLawyerMessageSent === 'function') {
          onLawyerMessageSent();
        }
        return;
      }
      const errText =
        (res && (res.message || res.error)) ||
        (typeof res?.message === 'string' ? res.message : null) ||
        'Send failed';
      throw new Error(typeof errText === 'string' ? errText : 'Send failed');
    },
    [projectId, appendIfNew, onLawyerMessageSent, ownerEmail],
  );

  const showFileActions =
    Boolean(projectFilesUrl) || typeof onAddProjectFilesClick === 'function';

  const isDescriptionOnlyThread =
    messages.length === 1 && messages[0]?.id === 'project-description';

  return (
    <div className={`${styles.convWrapper} lawyerProjectThread`}>
      <header className={styles.convHeader}>
        <div className={styles.convHeaderRow}>
          {canEditTitle || projectTitle ? (
            editingTitle ? (
              <input
                ref={titleInputRef}
                type="text"
                className={styles.convTitleInput}
                value={titleDraft}
                disabled={savingTitle}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={() => { void saveTitleEdit(); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void saveTitleEdit();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelTitleEdit();
                  }
                }}
                aria-label={t('project.projectName')}
              />
            ) : canEditTitle ? (
              <div className={styles.convTitleRow}>
                <h2
                  className={styles.convTitle}
                  onClick={startTitleEdit}
                  title={t('project.projectName')}
                >
                  <span className={styles.convTitleText}>
                    {projectTitle || t('project.projectNamePlaceholder')}
                  </span>
                </h2>
                <button
                  type="button"
                  className={`${styles.convTitleEditBtn} lawyer-project-thread__title-edit`}
                  onClick={startTitleEdit}
                  aria-label={t('project.projectName')}
                  title={t('project.projectName')}
                >
                  <i className="fa-solid fa-pen" aria-hidden="true" />
                </button>
              </div>
            ) : (
              <h2 className={styles.convTitle}>{projectTitle}</h2>
            )
          ) : null}
          {typeof onOpenProjectDetails === 'function' ? (
            <button
              type="button"
              className={`${styles.convFileAction} ${styles.convHeaderDetailsBtn} lawyer-project-thread__file-action lawyer-project-thread__details-action`}
              onClick={onOpenProjectDetails}
              aria-label="Open project details"
              title="Project details"
            >
              <InlineSvgIcon width={20} className="svg-icon" src="/svg/icon/info-i.svg" aria-hidden="true" />
            </button>
          ) : null}
          {showFileActions ? (
            <div className={styles.convHeaderFiles}>
              <div
                className={[
                  styles.fileActionsArea,
                  projectFilesUrl ? styles.fileActionsAreaHasView : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <ul>
                  {typeof onAddProjectFilesClick === 'function' ? (
                    <li className={projectFilesUrl ? styles.fileActionAddItem : undefined}>
                      <button
                        type="button"
                        className={[
                          styles.convFileAction,
                          styles.convFileActionAdd,
                          'lawyer-project-thread__file-action',
                          isUploadingProjectFiles ? styles.convFileActionUploading : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={onAddProjectFilesClick}
                        disabled={!canAddProjectFiles || isUploadingProjectFiles}
                        title={!canAddProjectFiles ? addProjectFilesTitle : undefined}
                        aria-label={
                          isUploadingProjectFiles
                            ? t('project.thread.uploadingFiles')
                            : t('project.thread.addFiles')
                        }
                      >
                        <InlineSvgIcon width={20} className="svg-icon" src="/svg/icon/file.svg" aria-hidden="true" />
                        <span className={styles.fileActionLabel}>
                          {isUploadingProjectFiles
                            ? t('project.thread.uploadingFiles')
                            : t('project.thread.addFiles')}
                        </span>
                      </button>
                    </li>
                  ) : null}
                  {projectFilesUrl ? (
                    <li>
                      <a
                        href={projectFilesUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${styles.convFileAction} ${styles.convFileActionView} lawyer-project-thread__file-action`}
                        aria-label={t('project.thread.viewFiles')}
                      >
                        <InlineSvgIcon width={20} className="svg-icon" src="/svg/icon/file.svg" aria-hidden="true" />
                        <span className={styles.fileActionLabel}>
                          {t('project.thread.viewFiles')}
                        </span>
                      </a>
                    </li>
                  ) : null}
                </ul>
              </div>
            </div>
          ) : null}
        </div>
        <div className={styles.convSeparator} aria-hidden="true" />
      </header>
      {isProjectStarted && (
        <LawyerProjectMessageComposer
          onSend={handleSend}
          initialDraft={initialComposerDraft}
          onDraftChange={handleComposerDraftChange}
          placeholder={t('project.thread.writePlaceholder')}
        />
      )}
      <div className={`${styles.convBody} lawyer-project-thread__conv-body`}>
        {messages.map((msg) => {
          const useOutgoingStyle =
            msg.type === 'lawyer' ||
            (isDescriptionOnlyThread && msg.id === 'project-description');
          if (useOutgoingStyle) {
            const statusTooltipText = t(resolveMessageStatusTooltipKey(msg));
            return (
              <OutgoingBubble
                key={msg.id}
                text={msg.text}
                files={msg.files}
                payload={msg.payload}
                sentAt={msg.sentAt || msg.receivedAt}
                authorLabel={msg.authorLabel}
                notificationSentAt={msg.notificationSentAt}
                notificationFailedAt={msg.notificationFailedAt}
                readAt={msg.readAt}
                statusTooltipText={statusTooltipText}
                isRetryingNotification={Boolean(msg.isRetryingNotification)}
                onShowTooltip={showMsgTooltip}
                onHideTooltip={hideMsgTooltip}
                onRetryNotification={handleRetryNotification}
              />
            );
          }
          return (
            <IncomingBubble
              key={msg.id}
              text={msg.text}
              files={msg.files}
              receivedAt={msg.receivedAt}
              authorLabel={msg.authorLabel}
              isUnreadByLawyer={Boolean(msg.isUnreadByLawyer)}
              playReadFlash={Boolean(msg.playReadFlash)}
              onReadFlashEnd={() => clearReadFlash(msg.id)}
            />
          );
        })}
      </div>
      {msgTooltip.visible && (() => {
        const tooltipMaxWidth = 200;
        const offset = 10;
        const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
        const wouldOverflowRight = msgTooltip.x + offset + tooltipMaxWidth > screenWidth;
        const leftPosition = wouldOverflowRight
          ? msgTooltip.x - tooltipMaxWidth - offset
          : msgTooltip.x + offset;
        return (
          <div
            className={styles.msgStatusTooltip}
            style={{
              left: `${leftPosition}px`,
              top: `${msgTooltip.y + offset}px`,
            }}
          >
            {msgTooltip.message}
          </div>
        );
      })()}
    </div>
  );
});

LawyerProjectConversation.displayName = 'LawyerProjectConversation';

export default LawyerProjectConversation;
